import { defineStore } from 'pinia';
import { getCustomAwardData } from '@webapp-next/modules/people/composables/pay-rates';
import { LeaveRule } from '@/modules/people/types/people-setup';
import { EmployeeContract } from '@/modules/people/types/employee';
import { computed, ComputedRef } from 'vue';
import { LibraryTemplateGetResponse } from '@/lib/request/webapp/people/library-template-get';
import getEmployeeLeaveEntitlementIdsByEmployeeId from '@/lib/request/webapp/people/employee-leave-entitlement-ids-get';
import { EmployeeLeaveEntitlmentsData } from '@/lib/request/webapp/people/employee-leave-entitlement-ids-get';
import { sortArrayAsc } from '@/modules/people/composables/sort';
import isDeputyXeroEmbed from '@/modules/integrations/facades/isDeputyXeroEmbed';

export type StateLeaveRuleOption = {
  link: LeaveRule['Id'] | undefined;
  value: LeaveRule['Name'];
};
export type StateLeaveRuleOptions = StateLeaveRuleOption[] | undefined;

type LeaveEntitlementState = {
  // leave rule IDs from employee contract
  employeeLeaveRules?: LeaveRule[];
  allLeaveRules?: LeaveRule[];
  employmentCondition: undefined;
  validationError: {
    leave: ComputedRef<string | undefined>;
    hours: ComputedRef<string | undefined>;
  };
  // leave rule IDs from UI
  entitledLeaveRules: LeaveRule['Id'][];
  // cached leave rule IDs from get leave entitlement v2 endpoint
  employeeLeaveEntitlementIds: number[] | [] | undefined;
  employeeEntitlementNamesArray?: (string | undefined)[] | undefined;
  employeeEntitlementIdAndNameArray?: StateLeaveRuleOptions;
  employeeCurrentLeaveEntitlementArray?: StateLeaveRuleOptions;
  employeeNewLeaveEntitlementArray?: StateLeaveRuleOptions;
  isPayRateChanged: boolean;
  employeeNewLeaveEntitlementIds: number[] | [] | undefined;
  employeeCurrentLeaveEntitlementIds: number[] | [] | undefined;
  defaultLeaveRules: StateLeaveRuleOption[] | undefined;
};

function getValidationErrorDefaults(): LeaveEntitlementState['validationError'] {
  return {
    leave: computed(() => ''),
    hours: computed(() => ''),
  };
}

const useLeaveEntitlementStore = defineStore(
  'webapp:people:leave-entitlement',
  {
    state: (): LeaveEntitlementState => {
      return {
        employeeLeaveRules: undefined,
        defaultLeaveRules: undefined,
        allLeaveRules: [],
        employmentCondition: undefined,
        validationError: getValidationErrorDefaults(),
        entitledLeaveRules: [],
        employeeLeaveEntitlementIds: [],
        employeeEntitlementNamesArray: [],
        employeeEntitlementIdAndNameArray: [],
        employeeCurrentLeaveEntitlementArray: [],
        employeeNewLeaveEntitlementArray: [],
        isPayRateChanged: false,
        employeeNewLeaveEntitlementIds: [],
        employeeCurrentLeaveEntitlementIds: [],
      };
    },
    actions: {
      async getEmployeeLeaveRulesByContractId(
        contractId: EmployeeContract['Id'],
        libraryTemplate: LibraryTemplateGetResponse['data']
      ): Promise<void> {
        if (!contractId) {
          throw new Error('contractId is required');
        }

        try {
          const { leaveRules } = await getCustomAwardData(
            contractId,
            libraryTemplate
          );
          this.setEmployeeLeaveRules(
            // TODO: remove this suppression after migrating composables/pay-rates.js to TS
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            leaveRules.map(award => award.LeaveRule) as LeaveRule[]
          );
        } catch (e) {
          console.error(e);
          throw new Error(e as string);
        }
      },
      setEmployeeLeaveRules(leaveRules: LeaveRule[] = []): void {
        this.employeeLeaveRules = leaveRules;
      },
      getEmployeeLeaveRules(): LeaveRule[] {
        return this.employeeLeaveRules || [];
      },
      setAllLeaveRules(leaveRules: LeaveRule[] = []): void {
        this.allLeaveRules = leaveRules;
      },
      getEmployeeLeaveRuleIdAndNameArrayFromContract(): StateLeaveRuleOptions {
        const leaveRuleIdAndNameArray: StateLeaveRuleOptions = [];
        const leaveRuleIds: LeaveRule['Id'][] = [];
        ((this.employeeLeaveRules || []) as LeaveRule[]).forEach(
          (item: LeaveRule) => {
            const itemId: LeaveRule['Id'] = item.LeaveRuleId
              ? item.LeaveRuleId
              : item.Id;

            const leaveRule: LeaveRule | undefined = ((this.allLeaveRules ||
              []) as LeaveRule[]).find((x: LeaveRule) => {
              return x.Id === itemId;
            });

            if (!leaveRule) {
              return;
            }

            leaveRuleIdAndNameArray?.push({
              link: itemId,
              value: leaveRule.Name,
            });
            leaveRuleIds.push(itemId);
          }
        );
        this.employeeNewLeaveEntitlementArray = leaveRuleIdAndNameArray;
        this.employeeNewLeaveEntitlementIds = sortArrayAsc(leaveRuleIds);
        return leaveRuleIdAndNameArray;
      },
      setEntitledLeaveRules(leaveRules: number[]): void {
        this.entitledLeaveRules = leaveRules;
      },
      setValidationError(
        id: keyof LeaveEntitlementState['validationError'],
        value: string
      ): void {
        this.validationError[id] = value;
      },
      getValidationError(
        id: keyof LeaveEntitlementState['validationError']
      ): string {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        return id && this.validationError[id] ? this.validationError[id] : '';
      },
      setValidationErrors(errors = getValidationErrorDefaults()): void {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        this.validationError = errors;
      },
      getValidationErrors(): LeaveEntitlementState['validationError'] {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        return this.validationError;
      },
      getEmployeeLeaveEntitlementIds(): number[] | [] | undefined {
        return this.employeeLeaveEntitlementIds;
      },
      setEmployeeLeaveEntitlementIds(
        employeeLeaveEntitlmentIds: number[] | [] | undefined
      ): number[] | [] | undefined {
        return (this.employeeLeaveEntitlementIds = employeeLeaveEntitlmentIds);
      },
      isEmployeeLeaveEntitlementIdsCached(): boolean {
        return this.getEmployeeLeaveEntitlementIds() !== undefined;
      },
      async cacheEmployeeLeaveEntitlementIdsByEmployeeId(
        employeeId: number
      ): Promise<number[] | [] | undefined> {
        if (this.isEmployeeLeaveEntitlementIdsCached()) {
          return this.getEmployeeLeaveEntitlementIds();
        }

        try {
          const result = (await getEmployeeLeaveEntitlementIdsByEmployeeId(
            employeeId
          )) as EmployeeLeaveEntitlmentsData;
          const leaveRuleIds = result.leaveRuleIds;

          this.setEmployeeLeaveEntitlementIds(leaveRuleIds);

          return this.getEmployeeLeaveEntitlementIds();
        } catch (e) {
          console.error(e);
        }
      },
      getEmployeeEntitlementIdAndNameArray():
        | StateLeaveRuleOptions
        | undefined {
        return this.employeeEntitlementIdAndNameArray;
      },
      setEmployeeEntitlementIdAndNameArray(
        entitlementIdAndNameArray: StateLeaveRuleOption[] | undefined
      ): StateLeaveRuleOptions | undefined {
        this.employeeEntitlementIdAndNameArray = entitlementIdAndNameArray;
        return this.employeeEntitlementIdAndNameArray;
      },
      async getLeaveEntitlementNamesByEmployeeId(
        employeeId: number
      ): Promise<(string | undefined)[] | undefined> {
        const entitlementIdAndNameArray: StateLeaveRuleOption[] = [];
        try {
          // Clear the cache
          this.setEmployeeLeaveEntitlementIds(undefined);

          const employeeEntitlementIds = await this.cacheEmployeeLeaveEntitlementIdsByEmployeeId(
            employeeId
          );

          if (employeeEntitlementIds?.length === 0) {
            this.setEmployeeEntitlementIdAndNameArray(undefined);
            return (this.employeeEntitlementNamesArray = undefined);
          }

          const entitlementIdSet = new Set(employeeEntitlementIds);

          this.employeeEntitlementNamesArray = this.allLeaveRules
            ?.filter(leaveRule => entitlementIdSet.has(leaveRule.Id))
            ?.map(leaveRule => {
              entitlementIdAndNameArray.push({
                link: leaveRule.Id,
                value: leaveRule.Name || '',
              });
              return leaveRule.Name || '';
            });

          this.setEmployeeEntitlementIdAndNameArray(entitlementIdAndNameArray);
          this.employeeCurrentLeaveEntitlementArray =
            this.employeeEntitlementIdAndNameArray ?? [];
          this.employeeCurrentLeaveEntitlementIds = employeeEntitlementIds;

          return this.employeeEntitlementNamesArray;
        } catch (e) {
          console.error(e);
          throw new Error(e as string);
        }
      },
    },
  }
);

export default useLeaveEntitlementStore;
